import { Grid, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Sort from '@mui/icons-material/Sort';
import {
  HeaderRow,
  HeaderTextWrapper,
  HeaderTitle,
  LoadMoreButton,
  LoadMoreWrapper,
  PageContainer,
  WelcomeName,
} from '~/modules/matters/MattersListLayout.styles';
import { useCallback, useMemo, useRef, useState } from 'react';
import { RetainerStatus } from '~/gql/graphql';
import { buildGetExpertByIdFromMatters } from '~/modules/matters/expertResolverFromMatters';
import { buildGetAttorneyByIdFromMatters } from '~/modules/matters/attorneyResolverFromMatters';
import { useSearchParams } from 'react-router-dom';
import { useMattersQuery } from '~/modules/matters/useMattersQuery';
import { MatterCard } from '~/modules/matters/MatterCard';
import { ExpertRetainerRollupStatus } from '~/modules/matters/MatterCard.types';
import { MattersEmptyView } from '~/modules/matters/MattersEmptyView';
import { MattersListSkeleton } from '~/modules/matters/MattersListSkeleton';
import { MattersListErrorState } from '~/modules/matters/MattersListErrorState';
import RequestRetainerModal from '~/modules/matters/modals/RequestRetainerModal';
import { PayoutOnboardingBanner } from '~/modules/matters/PayoutOnboardingBanner';
import { MattersTabs } from '~/modules/matters/MattersTabs';
import DropdownFilter from '~/modules/matters/DropdownFilter';
import ExpertMattersFilter from '~/modules/matters/ExpertMattersFilter';
import {
  useMattersList,
  SORT_OPTION_LABELS,
  SORT_OPTIONS,
  SortOption,
  FilterByOption,
} from '~/modules/matters';
import { getUserById } from '~/modules/matters/utils/userLookup';
import { getMatterCardDerivedProps } from '~/modules/matters/matterCardUtils';
import { getMatterExpertRowForRetainerRequest } from '~/modules/matters/matterExpertUtils';
import { attorneyUserFromMatterGraph } from '~/modules/matters/matterParticipantDisplay';
import type { MatterViewModel } from '~/modules/matters/matters.types';
import type { User } from '~/shared/types/connect';
import type { MattersTabValue } from '~/shared/types';

interface Props {
  currentUser: User;
}

const ExpertMattersView = ({ currentUser }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const attorneyFromUrl = searchParams.get('attorney') ?? null;
  const [attorneyFilterId, setAttorneyFilterIdState] = useState<string | null>(
    attorneyFromUrl,
  );
  const prevAttorneyFromUrl = useRef(attorneyFromUrl);
  if (prevAttorneyFromUrl.current !== attorneyFromUrl) {
    prevAttorneyFromUrl.current = attorneyFromUrl;
    setAttorneyFilterIdState(attorneyFromUrl);
  }
  const setAttorneyFilterId = (id: string | null) => {
    setAttorneyFilterIdState(id);
    if (id === null) {
      searchParams.delete('attorney');
    } else {
      searchParams.set('attorney', id);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const { matters, isLoading, error } = useMattersQuery();
  const getExpertById = useMemo(
    () => buildGetExpertByIdFromMatters(matters),
    [matters],
  );
  const getAttorneyById = useMemo(
    () => buildGetAttorneyByIdFromMatters(matters),
    [matters],
  );

  const getRetainersForMatter = useCallback(
    (targetMatterId: string) => {
      const found = matters.find(
        (matterItem) => matterItem.id === targetMatterId,
      );
      if (!found) {
        return [];
      }
      return found.matterExperts.flatMap((row) => row.retainers);
    },
    [matters],
  );

  const [mattersSubTab, setMattersSubTab] = useState<MattersTabValue>('active');
  const [requestRetainerMatterId, setRequestRetainerMatterId] = useState<
    string | null
  >(null);
  const [sortBy, setSortBy] = useState<SortOption>(SortOption.UpdatedAtDesc);
  const [filterBy, setFilterBy] = useState<FilterByOption>(FilterByOption.All);

  const {
    myMatters,
    filteredMatters,
    visibleMatters,
    hasMoreMatters,
    loadMore,
    activeCount,
    inactiveCount,
    closedCount,
    attorneyOptions,
  } = useMattersList({
    matters,
    currentUser,
    mattersSubTab,
    sortBy,
    filterBy,
    expertFilterId: null,
    attorneyFilterId,
    getRetainersForMatter,
    getExpertById,
    getAttorneyById,
  });

  function getTotalBilledForMatter(matterId: string): number {
    return (
      getRetainersForMatter(matterId)
        .filter((retainer) => retainer.status === RetainerStatus.Approved)
        .reduce((sum, retainer) => sum + retainer.total, 0) / 100
    );
  }

  const showPayoutBanner = currentUser?.stripeConnectOnboarded === false;

  const emptyStateMessage = useMemo(() => {
    if (showPayoutBanner) {
      return 'Connect your payout account to receive matter invitations; your matters will appear here.';
    }
    if (mattersSubTab === 'active') {
      return 'No active matters.';
    }
    if (mattersSubTab === 'inactive') {
      return 'No inactive matters.';
    }
    return 'No closed matters.';
  }, [showPayoutBanner, mattersSubTab]);

  const attorneyOptionsForFilter = useMemo(() => {
    const fromMatters = attorneyOptions.map((option) => ({
      id: option.id,
      name: option.attorney.name ?? 'Attorney',
    }));
    const hasSelectedInList =
      attorneyFilterId === null ||
      attorneyOptions.some((option) => option.id === attorneyFilterId);
    if (hasSelectedInList) {
      return fromMatters;
    }
    const urlAttorney = getAttorneyById(attorneyFilterId);
    return [
      {
        id: attorneyFilterId,
        name: urlAttorney?.name ?? 'Attorney',
      },
      ...fromMatters,
    ];
  }, [attorneyOptions, attorneyFilterId, getAttorneyById]);

  const matterForRetainer: MatterViewModel | null =
    requestRetainerMatterId === null
      ? null
      : matters.find((matter) => matter.id === requestRetainerMatterId) ?? null;
  const showRetainerModal = Boolean(
    matterForRetainer &&
      getMatterExpertRowForRetainerRequest(
        matterForRetainer.matterExperts,
        currentUser.id,
      ),
  );

  const showSkeleton = isLoading;
  const showError = !isLoading && Boolean(error);
  const showInitialEmpty = !isLoading && !error && myMatters.length === 0;
  const showContent = !showSkeleton && !showError && !showInitialEmpty;

  return (
    <PageContainer>
      <HeaderRow>
        <HeaderTextWrapper>
          <HeaderTitle variant="h5" fontWeight={700}>
            Welcome back{currentUser.name ? ',' : ''}
            {currentUser.name ? (
              <>
                {' '}
                <WelcomeName>{currentUser.name}</WelcomeName>
              </>
            ) : null}
          </HeaderTitle>
          <HeaderSubtitle variant="body2" color="text.secondary">
            Track your cases, communicate with attorneys, and manage retainers
          </HeaderSubtitle>
        </HeaderTextWrapper>
      </HeaderRow>

      {showSkeleton && <MattersListSkeleton />}
      {showError && (
        <MattersListErrorState
          message={error?.message || 'Failed to load matters'}
        />
      )}
      {showInitialEmpty && (
        <MattersEmptyView
          title={
            showPayoutBanner
              ? 'Connect your payout account to get invited to matters'
              : "You don't have any matters yet."
          }
          description={
            showPayoutBanner
              ? 'Set up your payout account; then your matters will appear here when an attorney invites you.'
              : 'Matters will appear here when an attorney invites you to one.'
          }
        >
          {showPayoutBanner ? <PayoutOnboardingBanner /> : null}
        </MattersEmptyView>
      )}
      {showContent && (
        <>
          {showPayoutBanner && <PayoutOnboardingBanner />}
          <MattersTabs
            value={mattersSubTab}
            onChange={(_event: React.SyntheticEvent, value: MattersTabValue) =>
              setMattersSubTab(value)
            }
            activeCount={activeCount}
            inactiveCount={inactiveCount}
            closedCount={closedCount}
            toolbar={
              <>
                <ExpertMattersFilter
                  attorneyFilterId={attorneyFilterId}
                  filterBy={filterBy}
                  attorneyOptions={attorneyOptionsForFilter}
                  onAttorneyChange={setAttorneyFilterId}
                  onFilterByChange={setFilterBy}
                />
                <DropdownFilter
                  ariaLabel="Sort by"
                  labelPrefix="Sort:"
                  valueLabel={SORT_OPTION_LABELS[sortBy]}
                  icon={<Sort fontSize="small" color="action" aria-hidden />}
                  options={SORT_OPTIONS}
                  onSelect={(value: SortOption) => setSortBy(value)}
                />
              </>
            }
          />

          {filteredMatters.length === 0 ? (
            <MattersEmptyView
              title={emptyStateMessage}
              description="Try adjusting your filters or sort order."
              compact
            />
          ) : (
            <MattersContentColumn>
              <Grid container spacing={2}>
                {visibleMatters.map((matter) => {
                  const derived = getMatterCardDerivedProps(matter, {
                    retainers: getRetainersForMatter(matter.id),
                    getExpertById,
                  });
                  const otherParty =
                    attorneyUserFromMatterGraph(matter.attorney) ??
                    getUserById(matter.attorneyId);
                  const otherExpertsOnMatter = derived.experts.filter(
                    (expert) => expert.id !== currentUser?.id,
                  );
                  const otherExpertRetainerStatuses =
                    derived.expertRetainerStatuses.filter(
                      (status) => status.expertId !== currentUser?.id,
                    );
                  let otherPartyRetainerStatus: ExpertRetainerRollupStatus;
                  if (derived.hasFundedRetainer) {
                    otherPartyRetainerStatus =
                      ExpertRetainerRollupStatus.Active;
                  } else if (derived.hasPendingRetainer) {
                    otherPartyRetainerStatus =
                      ExpertRetainerRollupStatus.PendingApproval;
                  } else {
                    otherPartyRetainerStatus =
                      ExpertRetainerRollupStatus.WaitingRequest;
                  }
                  return (
                    <Grid size={12} key={matter.id}>
                      <MatterCard
                        matter={matter}
                        otherParty={otherParty}
                        experts={otherExpertsOnMatter}
                        expertRetainerStatuses={otherExpertRetainerStatuses}
                        totalBilled={getTotalBilledForMatter(matter.id)}
                        hasPendingRetainer={derived.hasPendingRetainer}
                        hasFundedRetainer={derived.hasFundedRetainer}
                        isAttorneyView={false}
                        matterTab={mattersSubTab}
                        activeRetainerAmount={derived.activeRetainerAmount}
                        activeRetainerCount={derived.activeRetainerCount}
                        otherPartyRetainerStatus={otherPartyRetainerStatus}
                        onRequestRetainer={
                          getMatterExpertRowForRetainerRequest(
                            matter.matterExperts,
                            currentUser.id,
                          )
                            ? (matterParam: MatterViewModel) =>
                                setRequestRetainerMatterId(matterParam.id)
                            : undefined
                        }
                      />
                    </Grid>
                  );
                })}
              </Grid>
              {hasMoreMatters && (
                <LoadMoreWrapper>
                  <LoadMoreButton
                    variant="outlined"
                    size="medium"
                    onClick={loadMore}
                  >
                    Load more
                  </LoadMoreButton>
                </LoadMoreWrapper>
              )}
            </MattersContentColumn>
          )}
        </>
      )}

      {showRetainerModal && matterForRetainer && (
        <RequestRetainerModal
          open
          onClose={() => setRequestRetainerMatterId(null)}
          matterId={matterForRetainer.id}
          matterExpertId={
            getMatterExpertRowForRetainerRequest(
              matterForRetainer.matterExperts,
              currentUser.id,
            )?.id ?? null
          }
          expert={currentUser}
          rateSourceUser={currentUser}
        />
      )}
    </PageContainer>
  );
};

const HeaderSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.body1.fontSize,
  [theme.breakpoints.up('sm')]: {
    fontSize: theme.typography.body2.fontSize,
  },
}));

const MattersContentColumn = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

export { ExpertMattersView };
